import { ValueAnchor, RecorderClock } from './interpolation';
import { PointOfInterest } from './point-of-interest';
import { OverlaySettings } from '../overlay-settings';

export enum EStateKeys {
    MISC_STATE = 'misc',
    SETTINGS_STATE = 'settings',
    HEROES_STATE = 'heroes',
    GAME_STATE = 'game',
    UPGRADES_STATE = 'upgrades',
    RESOURCES_STATE = 'resources',
    UNITS_STATE = 'units',
    PRODUCTION_STATE = 'production',
    POIS_STATE = 'pois',
}

export interface IStateUpdate {
    time: Date;
    isSnapshot: boolean;
    stateType: EStateKeys;
    differences?: Array<any>;
    snapshot?: IHeroState | IGameState;
}

export interface IStateWrapper extends IPlayerStateWrapper {
    misc: IMiscState;
    settings: ISettingsState;
    game: IGameState;
    pois?: Readonly<Record<string, PointOfInterest>>;
    poiMode?: 'initial' | 'live';
    units?: { [instanceId: string]: IUnitHealthState };
    production?: { [buildingId: string]: IProductionQueueState };
}

/** Uniform game-time snapshot; unavailable timing is explicitly null. */
export interface ITimedProgress {
    readonly timing?: ValueAnchor;
    readonly progress: number | null;
    readonly remainingSeconds: number | null;
    readonly totalSeconds: number | null;
}

/** Queue positions are replaced with each snapshot; they are not persistent job IDs. */
export interface IProductionQueueState {
    isIllusion: boolean;
    id: string;
    slotId: number;
    typeId: string;
    queue: Array<ITimedProgress & { position: number; typeId: string; buildType?: 'research' | 'unit' | 'reviving' }>;
}

/** Recorder-owned damage accounting for the hero and its attributed summons. */
export interface IHeroDamageSummary {
  readonly total: number;
  readonly breakdown: ReadonlyArray<{
    readonly units: ReadonlyArray<{ readonly typeId: string; readonly isIllusion: boolean }>;
    readonly damageDealt: number;
  }>;
  /** False when recording history, attribution or source observations have gaps. */
  readonly complete: boolean;
}
/** Engine lifetime totals in actual HP after mitigation and overkill/overheal caps.
 * Includes neutral targets and friendly fire. Replay seeks replace observations. */
export interface IHeroCombatTotals {
  readonly damage?: IHeroDamageSummary;
  /** Original engine counter; use damage.total for summon-inclusive accounting. */
  readonly damageDealt: number;
  /** Damage where source and target are this same unit. Included in both damage totals. */
  readonly selfDamage: number;
  readonly damageReceived: number;
  /** Actual healing attributed by Warcraft to this unit, including self-healing; excludes ordinary regeneration.
   * No exact healing-to-others partition is currently available. */
  readonly healingDealt: number;
}

/** Match-scoped engine instance ID; never an address or a JavaScript number. */
export interface IUnitHealthState {
    combat?: IHeroCombatTotals;
    isIllusion: boolean;
    targetFlags: number;
    construction?: ITimedProgress;
    upgrade?: ITimedProgress & { readonly typeId: string };
    position?: { x: number; y: number };
    id: string;
    slotId: number;
    typeId: string;
    hitpoints: { current: number; max: number; timing?: ValueAnchor };
    mana?: { current: number; max: number; regenerationPerSecond?: number; timing?: ValueAnchor };
}

export interface IPlayerStateHeroes { [playerId: number]: IHeroesState }
export interface IPlayerStateUpgrades { [playerId: number]: IUpgradeState }
export interface IPlayerStateResources { [playerId: number]: IPlayerResources }

export interface IPlayerStateWrapper {
    heroes: IPlayerStateHeroes;
    upgrades: IPlayerStateUpgrades;
    resources: IPlayerStateResources;
}

export interface IHeroesState {
    [hero: string]: IHeroState;
}

export interface IHeroState {
    combat?: IHeroCombatTotals;
    /** Native hero-bar order key, not a spawn time; omitted until observed. */
    heroOrder?: number;
    isIllusion: boolean;
    id?: string;
    typeId?: string;
    experience: number;
    hitpoints?: {
        max: number;
        current: number;
    };
    mana?: {
        max: number;
        current: number;
        regenerationPerSecond?: number;
    };
    abilities: Array<{
        id: string,
        typeId: string,
        level: number,
        lastActivation: number
    }>;
    items: Array<string>;
    inventoryCooldowns?: Array<ITimedProgress | null>;
    inventoryCharges?: Array<number | null>;
}

export interface IUpgradeState {
    upgrades: Array<{
        typeId: string,
        level: number,
        gametime: number,
    }>;
    active: Array<{
        typeId: string,
        level: number,
        gametime: number,
    }>;
    researching: Array<ITimedProgress & {
        typeId: string,
        level: number,
        gametime: number,
    }>;
}

export type IPlayerResources = {
    [resource in EResource]?: number;
};

export enum ERace {
    RANDOM = 0,
    HUMAN = 1,
    ORC = 2,
    UNDEAD = 3,
    NIGHT_ELF = 4,
}

export enum EResource {
		GOLD = 1,
		LUMBER = 2,
		SUPPLY = 5,
		SUPPLY_CAP = 4,
		WORKER_SUPPLY = 99,
}

export interface IPlayerStats { status: 'loading' | 'ready' | 'unavailable'; records: IPlayerStatsObject[]; observedAt?: number; source?: 'live' | 'cache'; }

export interface IPlayerStatsObject {
    provider: 'bnet' | 'w3champions';
    gameMode: '1v1' | '2v2' | '3v3' | '4v4' | 'ffa';
    queue: 'individual' | 'arranged';
    season?: number;
    race?: 'random' | 'human' | 'orc' | 'undead' | 'night-elf';
    team?: { id: string; members: { id?: string; battleTag: string; gatewayId?: number }[] };
    mmr?: number;
    wins: number;
    losses: number;
    winRate: number;
    level?: number;
    rank?: number;
    league?: number | string;
    xp?: number;
    isPlaced?: boolean;
}

export interface IMainAccount {
    name: string;
    country?: string;
    mainRace?: ERace;
}

/** Cumulative engine player counters (own player in self-play). Missing is unavailable, never zero. */
export interface PlayerStatistics {
  /** Native handicap multiplier expressed as a percentage (100 = normal). */
  readonly handicapPercent?: number;
  /** Native lobby preference bit flags, and the actual native race enum. */
  readonly racePreference?: number;
  readonly playerRace?: number;
  readonly gameTime: number;
  readonly realTimeApm?: number;
  readonly slotState?: 0 | 1 | 2;
  readonly aiDifficulty?: 0 | 1 | 2;
  /** Ten native upkeep-tier durations in milliseconds; engine clock semantics. */
  readonly timeInUpkeepMs?: ReadonlyArray<number>;
  readonly goldMined?: number;
  readonly goldUpkeepLost?: number;
  readonly goldCredited?: number;
  readonly goldDiversionTax?: number;
  readonly lumberCredited?: number;
  readonly lumberUpkeepLost?: number;
  readonly lumberDiversionTax?: number;
  readonly items?: Readonly<Record<string, ItemStatistics>>;
  /** Full hero instance IDs, scoped with heroes:read. */
  readonly heroes?: Readonly<Record<string, HeroStatistics>>;
  /** Nonhero, nonbuilding types, scoped with units:read. */
  readonly units?: Readonly<Record<string, UnitTypeStatistics>>;
  /** Raw nonhero/nonbuilding counters by full instance ID, including retained dead units. Omitted on incomplete reads. */
  readonly unitInstances?: Readonly<Record<string, UnitInstanceStatistics>>;
}
export interface AbilityStatistics {
  readonly level?: number;
  readonly isHeroAbility?: boolean;
  /** Observed active engine timer; omitted when unavailable or inactive. */
  readonly cooldown?: ITimedProgress;
  readonly damageDealt: number;
  readonly healingDone: number;
}
export interface HeroStatistics {
  readonly typeId: string;
  readonly level?: number;
  readonly nextLevelExperience?: number;
  readonly deaths: number;
  readonly totalKills: number;
  readonly heroKills: number;
  readonly selfKills: number;
  readonly buildingKills: number;
  /** Engine wall-clock milliseconds alive, including replay pauses. Not match time. */
  readonly timeAliveMs: number;
  readonly abilities?: Readonly<Record<string, AbilityStatistics>>;
}
/** Engine lifetime counters for one observed ordinary unit, scoped with units:read. */
export interface UnitInstanceStatistics {
  readonly typeId: string;
  readonly alive: boolean;
  readonly damageDealt: number;
  readonly damageReceived: number;
  /** Warcraft-attributed healing, including self healing; ordinary regeneration is excluded. */
  readonly healingDone: number;
}
export interface UnitTypeStatistics {
  readonly currentAmount: number;
  /** Objects retained by the engine, including corpses; not lifetime units trained. */
  readonly totalAmount: number;
  readonly isPeon: boolean;
  /** At least one retained unit passes Warcraft's functional-worker predicate. */
  readonly isFunctionalPeon: boolean;
  /** Sum across retained instances; can decrease when corpses disappear. */
  readonly damageDealt: number;
  readonly damageReceived: number;
  readonly healingDone: number;
}
export interface ItemStatistics {
  readonly collected: number;
  readonly purchased: number;
  readonly sold: number;
  readonly used: number;
  /** Includes removal after consuming the last charge; not an additional item loss. */
  readonly destroyed: number;
  readonly damageDealt?: number;
  readonly healingDone?: number;
}
/** Cumulative sampled deaths, excluding heroes and illusions. Disappearance is not death. */
export interface PlayerLosses {
  readonly gameTime: number;
  readonly units?: Readonly<Record<string, number>>;
  readonly buildings?: Readonly<Record<string, number>>;
  /** False when deaths may be missed between recorder samples or before attachment. */
  readonly complete: boolean;
}
export type MatchOutcomes = Readonly<Record<string, 'won' | 'lost' | 'draw'>>;

export interface IPlayer {
    statistics?: PlayerStatistics;
    losses?: PlayerLosses;
    id: number;
    name: string;
    race: ERace;
    team: number;
    startPosition?: {
        x: number;
        y: number;
    };
    colorId: number;
    isAI: boolean;
    stats?: IPlayerStats;
    mainAccount?: IMainAccount;
    controlgroups?: { [id: number]: ICommandGroup };
    ressources?: IPlayerRessources;
    /** Native Warcraft APM; absent if unavailable. */
    apm?: number;
}

export interface IPlayerRessources {
    gold: number;
    lumber: number;
    food: number;
    food_cap: number;
    worker_food: number;
}

export interface ICommandGroup {
    size: number;
    frontunit: string;
    members?: ReadonlyArray<{ readonly unitId: string; readonly typeId: string }>;
}

export enum EGameStateEnum {
    GAME_START,
    GAME_RUNNING,
    GAME_FINISHED,
    NO_GAME
}


export enum EGameModeEnum {
    GM_UNDEFINED = 'undefined',
    GM_1V1 = 'gm-1v1',
    GM_2V2 = 'gm-2v2',
    GM_3V3 = 'gm-3v3',
    GM_4V4 = 'gm-4v4',
    GM_3FFA = 'gm-3ffa',
    GM_4FFA = 'gm-4ffa',
    GM_CUSTOM = 'gm-custom',
}

export class IGameState {
    gameName?: string;
    clock?: RecorderClock;
    gameDataId?: string;
    gameVersion?: string;
    /** Private recorder correlation; never contains connection credentials. */
    statsContext?: string;
    id: number;
    state: EGameStateEnum;
    start?: Date;
    end?: Date;
    result?: { playerId: string; outcome: 'won' | 'lost' };
    outcomes?: MatchOutcomes;
    isReplay?: boolean;
    isReforged?: boolean;
    isObserver?: boolean;
    players: { [id: number]: IPlayer };
    realm?: string;
    broadcasterId?: number;
    realBroadcasterId?: number;
    map?: string;
    pausedTime: number;
    gameTime: number;
    paused: boolean;
    gameMode: EGameModeEnum;
}

export class IMiscState {
    chatbarOpen: boolean;
    menuOpen?: boolean;
    advancedObserverMode?: boolean;
    hudScale: number;
    heroBarLastOccupiedSlot?: number;
    teamColors: boolean;
    localServerUrls: Array<string>;
}


export class ISettingsState extends OverlaySettings {
}

export const raceNames = new Map().set(ERace.HUMAN, 'Human')
    .set(ERace.NIGHT_ELF, 'Night Elf')
    .set(ERace.ORC, 'Orc')
    .set(ERace.UNDEAD, 'Undead')
    .set(ERace.RANDOM, 'Random');

export const raceShortNames = new Map().set(ERace.HUMAN, 'HU')
    .set(ERace.NIGHT_ELF, 'NE')
    .set(ERace.ORC, 'ORC')
    .set(ERace.UNDEAD, 'UD')
    .set(ERace.RANDOM, 'RDM');


/** Public protocol 4 observations. The standalone SDK mirrors this wire contract. */
export interface IAppUnit {
    readonly isIllusion: boolean;
    readonly id: string;
    readonly typeId: string;
    readonly hitpoints?: { readonly current: number; readonly max: number };
    readonly mana?: { readonly current: number; readonly max: number; readonly regenerationPerSecond?: number };
    readonly position?: { readonly x: number; readonly y: number };
}
export interface IAppHero extends IAppUnit {
    readonly combat?: IHeroCombatTotals;
    readonly heroOrder?: number;
    readonly level?: number;
    readonly experience?: number;
    readonly abilities?: readonly { readonly id: string; readonly typeId: string; readonly level: number; readonly lastActivation?: number }[];
    readonly inventory?: readonly string[];
    readonly inventoryCooldowns?: readonly (ITimedProgress | null)[];
    readonly inventoryCharges?: readonly (number | null)[];
}
export interface IAppBuilding extends IAppUnit {
    readonly construction?: ITimedProgress;
    readonly upgrade?: ITimedProgress & { readonly typeId: string };
    readonly production?: { readonly queue: readonly (ITimedProgress & { readonly position: number; readonly typeId: string })[] };
}
export interface IPlayerUnitCollections {
    readonly units?: Readonly<Record<string, IAppUnit>>;
    readonly heroes?: Readonly<Record<string, IAppHero>>;
    readonly buildings?: Readonly<Record<string, IAppBuilding>>;
}
